/**
 * The delivery-job mini route (#2588): four stops (planned, dispatched, in
 * progress, completed) and the status label.
 *
 * The stops reached so far and the segments already walked take the colour of
 * the CURRENT status; the rest stay on `--border`. Exception, cancelled and an
 * unknown value are off the route, so nothing is lit. Status colour goes on
 * dots and lines only; the label uses the body colour (ADR-0043 §3).
 *
 * `data-testid="enum-badge-<status>"` keeps the default enum cell's test id.
 */
import { Fragment } from 'react';
import { cn } from '@resource-kit/ui';
import { JOB_ROUTE_STOPS, JOB_STATUS_TONE } from '@lib/job-status-tone';

export function JobStatusRoute({ status, label }: { status: string; label: string }) {
  const index = (JOB_ROUTE_STOPS as readonly string[]).indexOf(status);
  const current = index >= 0 ? JOB_ROUTE_STOPS[index] : undefined;
  const tone = current ? JOB_STATUS_TONE[current] : null;
  return (
    <span
      data-testid={`enum-badge-${status}`}
      className="inline-flex items-center gap-2 whitespace-nowrap"
    >
      <span aria-hidden="true" className="flex items-center">
        {JOB_ROUTE_STOPS.map((stop, k) => (
          <Fragment key={stop}>
            <span
              data-slot="route-stop"
              className={cn(
                'size-2 shrink-0 rounded-full border-2',
                tone && k <= index ? cn(tone.ring, tone.dot) : 'border-border',
              )}
            />
            {k < JOB_ROUTE_STOPS.length - 1 && (
              <span
                data-slot="route-segment"
                className={cn('h-0.5 w-2.5', tone && k < index ? tone.dot : 'bg-border')}
              />
            )}
          </Fragment>
        ))}
      </span>
      <span className="font-medium text-foreground">{label}</span>
    </span>
  );
}
